import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, Download, ExternalLink, Bell, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * UpdateNotificationBanner
 * 
 * Shows a notification banner when updates are available.
 * - For SaaS: Updates are automatic, shows info only
 * - For White-Label: Shows update instructions
 * 
 * Props:
 * - currentVersion: The installed version (default: from API)
 * - channel: 'saas' or 'white-label' (default: auto-detect)
 * - checkInterval: How often to check for updates in ms (default: 1 hour)
 * - onDismiss: Callback when banner is dismissed
 */
const UpdateNotificationBanner = ({ 
  currentVersion,
  channel,
  checkInterval = 3600000, // 1 hour
  onDismiss,
  className = ''
}) => {
  const [updateInfo, setUpdateInfo] = useState(null);
  const [deploymentInfo, setDeploymentInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkForUpdates();
    
    // Set up periodic checking
    const interval = setInterval(checkForUpdates, checkInterval);
    return () => clearInterval(interval);
  }, [currentVersion, channel]);

  const checkForUpdates = async () => {
    try {
      // First get deployment info if not provided
      let deploymentChannel = channel;
      if (!deploymentChannel) {
        const deployRes = await fetch(`${API_URL}/api/version/deployment-info`);
        if (deployRes.ok) {
          const deployData = await deployRes.json();
          setDeploymentInfo(deployData);
          deploymentChannel = deployData.channel;
        }
      }

      // Get current version if not provided
      let version = currentVersion;
      if (!version) {
        const versionRes = await fetch(`${API_URL}/api/version/current?channel=${deploymentChannel || 'saas'}`);
        if (versionRes.ok) {
          const versionData = await versionRes.json();
          version = versionData.version;
        }
      }

      // Check for updates
      const checkRes = await fetch(
        `${API_URL}/api/version/check?current_version=${version || '1.0.0'}&channel=${deploymentChannel || 'white-label'}`
      );
      
      if (checkRes.ok) {
        const data = await checkRes.json();
        setUpdateInfo(data);
      }
    } catch (error) {
      console.error('Failed to check for updates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    // Store dismissal in localStorage with version to not show again for same update
    if (updateInfo?.latest_version) {
      localStorage.setItem('dismissed_update', updateInfo.latest_version);
    }
    onDismiss?.();
  };

  // Check if this update was already dismissed
  useEffect(() => {
    if (updateInfo?.latest_version) {
      const dismissedVersion = localStorage.getItem('dismissed_update');
      if (dismissedVersion === updateInfo.latest_version) {
        setDismissed(true);
      }
    }
  }, [updateInfo]);

  // Don't render if loading, dismissed, or no update available
  if (loading || dismissed || !updateInfo?.update_available) {
    return null;
  }

  const isWhiteLabel = deploymentInfo?.is_white_label || channel === 'white-label';
  const isCritical = !updateInfo.is_supported;

  return (
    <div className={`relative ${className}`}>
      {/* Main Banner */}
      <div 
        className={`
          px-4 py-3 flex items-center justify-between gap-4
          ${isCritical 
            ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white' 
            : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
          }
        `}
      >
        <div className="flex items-center gap-3">
          {isCritical ? (
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          ) : (
            <Bell className="h-5 w-5 flex-shrink-0" />
          )}
          
          <div>
            <p className="font-medium">
              {isCritical ? 'Critical Update Required' : 'Update Available'}
              <span className="ml-2 px-2 py-0.5 bg-white/20 rounded text-sm">
                v{updateInfo.latest_version}
              </span>
            </p>
            <p className="text-sm opacity-90">
              {isCritical 
                ? `Your version (${updateInfo.current_version}) is no longer supported.`
                : `New version available. You're on v${updateInfo.current_version}.`
              }
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* View Changelog Button */}
          <Button
            size="sm"
            variant="ghost"
            className="text-white hover:bg-white/20"
            onClick={() => setShowChangelog(!showChangelog)}
          >
            {showChangelog ? 'Hide' : "What's New"}
          </Button>

          {/* Action Button for White-Label */}
          {isWhiteLabel && (
            <Button
              size="sm"
              className="bg-white text-blue-600 hover:bg-white/90"
              onClick={() => window.open('/docs/DEPLOYMENT_GUIDE.md', '_blank')}
            >
              <Download className="h-4 w-4 mr-1" />
              Update Guide
            </Button>
          )}

          {/* Dismiss Button */}
          {!isCritical && (
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-white/20 rounded"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Changelog Dropdown */}
      {showChangelog && updateInfo.changelog && (
        <div className="absolute top-full left-0 right-0 bg-card border border-border shadow-lg z-50 p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold">
              What's New in v{updateInfo.latest_version}
            </h4>
            <span className="text-sm text-muted-foreground">
              {updateInfo.changelog.date}
            </span>
          </div>
          
          <ul className="space-y-2">
            {updateInfo.changelog.changes?.map((change, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>{change}</span>
              </li>
            ))}
          </ul>

          {isWhiteLabel && updateInfo.update_instructions && (
            <div className="mt-4 p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-1">Update Instructions:</p>
              <code className="text-xs bg-background px-2 py-1 rounded">
                {updateInfo.update_instructions}
              </code>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Compact version indicator for sidebar/header
 */
export const VersionBadge = ({ className = '' }) => {
  const [versionInfo, setVersionInfo] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const res = await fetch(`${API_URL}/api/version/current`);
        if (res.ok) {
          const data = await res.json();
          setVersionInfo(data);
        }
        
        // Check for updates
        const checkRes = await fetch(`${API_URL}/api/version/check?current_version=${versionInfo?.version || '1.0.0'}`);
        if (checkRes.ok) {
          const checkData = await checkRes.json();
          setUpdateAvailable(checkData.update_available);
        }
      } catch (error) {
        console.error('Failed to fetch version:', error);
      }
    };

    fetchVersion();
  }, []);

  if (!versionInfo) return null;

  return (
    <div className={`flex items-center gap-2 text-xs ${className}`}>
      <span className="text-muted-foreground">
        v{versionInfo.version}
      </span>
      {updateAvailable && (
        <span className="px-1.5 py-0.5 bg-blue-500 text-white rounded text-[10px] font-medium">
          UPDATE
        </span>
      )}
    </div>
  );
};

export default UpdateNotificationBanner;
